import type {UseEmojiPickerType} from '@platejs/emoji/react';
import styles from "./emoji.module.scss";

import {type Emoji, type GridRow, EmojiSettings} from '@platejs/emoji';
import {memo, useCallback} from "react";

export type EmojiButtonProps = {
    emoji: Emoji;
    index: number;
    onMouseOver: (emoji?: Emoji) => void;
    onSelect: (emoji: Emoji) => void;
};

export type EmojiPickerContentProps = Pick<
    UseEmojiPickerType,
    | 'emojiLibrary'
    | 'i18n'
    | 'isSearching'
    | 'onMouseOver'
    | 'onSelectEmoji'
    | 'refs'
    | 'searchResult'
    | 'settings'
    | 'visibleCategories'
>;

export type RowOfButtonsProps = {
    row: GridRow;
} & Pick<UseEmojiPickerType, 'emojiLibrary' | 'onMouseOver' | 'onSelectEmoji'>;

const Button = memo(
    ({emoji, index, onMouseOver, onSelect}: EmojiButtonProps) => {
        return (
            <button
                onClick={() => onSelect(emoji)}
                onMouseEnter={() => onMouseOver(emoji)}
                onMouseLeave={() => onMouseOver()}
                aria-label={emoji.skins[0].native}
                data-index={index}
                tabIndex={-1}
                type="button"
            >
                <span
                    className="relative"
                    style={{
                        fontFamily:
                            '"Apple Color Emoji", "Segoe UI Emoji", NotoColorEmoji, "Noto Color Emoji", "Segoe UI Symbol", "Android Emoji", EmojiSymbols',
                    }}
                    data-emoji-set="native"
                >
          {emoji.skins[0].native}
        </span>
            </button>
        );
    }
);
Button.displayName = 'Button';

const RowOfButtons = memo(
    ({emojiLibrary, row, onMouseOver, onSelectEmoji}: RowOfButtonsProps) => (
        <div key={row.id} className={styles.emojiRow} data-index={row.id}>
            {row.elements.map((emojiId, index) => (
                <Button
                    key={emojiId}
                    onMouseOver={onMouseOver}
                    onSelect={onSelectEmoji}
                    emoji={emojiLibrary.getEmoji(emojiId)}
                    index={index}
                />
            ))}
        </div>
    )
);
RowOfButtons.displayName = 'RowOfButtons';

export function EmojiPickerContent({
                                       emojiLibrary,
                                       i18n,
                                       isSearching = false,
                                       refs,
                                       searchResult,
                                       settings = EmojiSettings,
                                       visibleCategories,
                                       onMouseOver,
                                       onSelectEmoji,
                                   }: EmojiPickerContentProps) {
    const getRowWidth = settings.perLine.value * settings.buttonSize.value;

    const isCategoryVisible = useCallback(
        (categoryId: any) => {
            return visibleCategories.has(categoryId)
                ? visibleCategories.get(categoryId)
                : false;
        },
        [visibleCategories]
    );

    const EmojiList = useCallback(() => {
        return emojiLibrary
            .getGrid()
            .sections()
            .map(({id: categoryId}) => {
                const section = emojiLibrary.getGrid().section(categoryId);
                const {buttonSize} = settings;

                return (
                    <div
                        key={categoryId}
                        ref={section.root}
                        data-id={categoryId}
                    >
                        <div className={styles.emojiTitle}>
                            {i18n.categories[categoryId]}
                        </div>
                        <div style={{height: section.getRows().length * buttonSize.value}}>
                            {isCategoryVisible(categoryId) &&
                                section
                                    .getRows()
                                    .map((row: GridRow) => (
                                        <RowOfButtons
                                            key={row.id}
                                            onMouseOver={onMouseOver}
                                            onSelectEmoji={onSelectEmoji}
                                            emojiLibrary={emojiLibrary}
                                            row={row}
                                        />
                                    ))}
                        </div>
                    </div>
                );
            });
    }, [
        emojiLibrary,
        getRowWidth,
        i18n.categories,
        isCategoryVisible,
        onSelectEmoji,
        onMouseOver,
        settings,
    ]);

    const SearchList = useCallback(() => {
        // Group search results into chunks of 8
        const groupedResults: Emoji[][] = [];
        for (let i = 0; i < searchResult.length; i += 8) {
            groupedResults.push(searchResult.slice(i, i + 8));
        }

        return (
            <div data-id="search">
                <div className={styles.emojiTitle}>
                    {i18n.searchResult}
                </div>
                <div>
                    {groupedResults.map((group, groupIndex) => (
                        <RowOfButtons
                            row={{
                                id: groupIndex,
                                elements: group.map((emoji) => emoji.id),
                            }}
                            onMouseOver={onMouseOver}
                            onSelectEmoji={onSelectEmoji}
                            emojiLibrary={emojiLibrary}
                        >
                        </RowOfButtons>
                    ))}
                </div>
            </div>
        );
    }, [
        emojiLibrary,
        getRowWidth,
        i18n.searchResult,
        searchResult,
        onSelectEmoji,
        onMouseOver,
    ]);

    return (
        <div
            ref={refs.current.contentRoot}
            data-id="scroll"
            className={styles.emojiContent}
        >
            <div ref={refs.current.content}>
                {isSearching ? SearchList() : EmojiList()}
            </div>
        </div>
    );
}
